import axios, { AxiosResponse, CancelToken } from 'axios';
import React, { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  HOME_ADDRESS,
  CITY,
  HOUSE_NUMBER,
  STREET,
  POST_CODE,
  HOUSE_NUMBER_ADDITION,
} from '~/actions/actionTypes';
import { setInvalidError, setMissingError } from '~/actions/errorActions';
import { setHomeAddressRequestStatus, setInput, validateValue } from '~/actions/formActions';
import { setIsHouseNumberRequired } from '~/actions/widgetContextActions';
import useDebounce from '~/hooks/useDebounce';
import { getDefaultLabelName } from '~/services/DefaultLabelNameService';
import { IState } from '~/sharedInterfaces/IState';
import { getInputClassNames, getUrls } from '~/utils';
import { IBaseComponent } from '../../sharedInterfaces/IBaseComponent';
import { IBaseInput, IDropDownOption } from '../../sharedInterfaces/SharedInterfaces';
import Input from '../Base/Input/Input';
import Tooltip from '../Base/Tooltip/Tooltip';
import ComponentError from '../ComponentError/ComponentError';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';

export interface IHomeAddress extends IBaseInput {
  postCode?: string;
  houseNumber?: string;
  houseNumberAddition?: string;
  city?: string;
  street?: string;
  homeAddressRequestStatus?: string;
}

export interface IHomeAddressProps extends IBaseComponent, IHomeAddress {}

const HomeAddress: FC<IHomeAddressProps> = (props) => {
  const dispatch = useDispatch();
  const value = useSelector((state: IState) => state.signup.homeAddress);
  const valuePostCode = value?.postCode || '';
  const valueHouseNumber = value?.houseNumber || '';
  const valueCity = value?.city || '';
  const valueStreet = value?.street || '';
  const valueHouseNumberAddition = value?.houseNumberAddition || '';
  const homeAddressRequestStatus = value?.homeAddressRequestStatus || 'idle';
  const homeAddressRequestIsPending = homeAddressRequestStatus === 'pending';

  const errorHomeAddress = useSelector((state: IState) => state.error.homeAddress?.invalid);
  const errorPostCode = useSelector((state: IState) => state.error.postCode);
  const errorHouseNumberAddition = useSelector((state: IState) => state.error.houseNumberAddition);
  const errorHouseNumber = useSelector((state: IState) => state.error.houseNumber?.missing);
  const { locale } = useSelector((state: IState) => state.widgetContext);
  const {
    label,
    city,
    houseNumber,
    houseNumberAddition,
    postCode,
    street,
    tooltipText,
    noAddressFound,
  } = getDefaultLabelName(locale, props.componentName);

  const [houseNumberAdditions, setHouseNumberAdditions] = useState<Array<IDropDownOption>>([]);
  const [suggestedHouseNumberAdditions, setSuggestedHouseNumberAdditions] = useState<
    Array<IDropDownOption>
  >([]);
  const [isHouseNumberAdditionValid, setIsHouseNumberAdditionValid] = useState(false);
  const [isSuggestionsOpen, setSuggestionsOpen] = useState(false);
  const refSuggestionList = useRef<HTMLUListElement>(null);
  const refHouseNumberAdditionInput = useRef<HTMLUListElement>(null);

  const hasValidAddress: boolean = valueCity.length > 0 && valueStreet.length > 0;
  const showHouseNumberAddition: boolean = !!houseNumberAdditions.length;
  const showHouseNumberAdditionSuggestions: boolean =
    suggestedHouseNumberAdditions.length > 0 &&
    valueHouseNumberAddition !== suggestedHouseNumberAdditions[0].value;

  const debouncedPostCodeValue = useDebounce(valuePostCode);
  const debouncedHouseNumberValue = useDebounce(valueHouseNumber);

  const formatPostCode = valuePostCode
    .replace(/[^a-zA-Z0-9]/g, '')
    .replace(/(.{4})/g, '$1 ')
    .trim()
    .toUpperCase();

  const handleClick = (event: MouseEvent) => {
    // Close suggestion list on click outside of it, allow click inside input to keep suggestion list open.
    if (refSuggestionList.current && !refSuggestionList.current.contains(event.target as any)) {
      setSuggestionsOpen(false);
    }

    if (
      refHouseNumberAdditionInput.current &&
      refHouseNumberAdditionInput.current.contains(event.target as any)
    ) {
      setSuggestionsOpen(true);
    }
  };

  const validateAdditionalHouseNumber = () => {
    return houseNumberAdditions.some(
      (number: IDropDownOption) => number.value === valueHouseNumberAddition
    );
  };

  const handleOnChangeHouseNumber = (event: ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    dispatch(setInput(HOUSE_NUMBER, inputValue));
    if (errorHouseNumber) dispatch(setMissingError(HOUSE_NUMBER, ''));
  };

  const handleOnChangePostCode = (event: ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    dispatch(setInput(POST_CODE, inputValue));
    if (errorPostCode?.missing) dispatch(setMissingError(POST_CODE, ''));
  };

  const handleOnChangeHouseAdditions = (event: ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    dispatch(setInput(HOUSE_NUMBER_ADDITION, inputValue));
    if (errorHouseNumberAddition?.missing) dispatch(setMissingError(HOUSE_NUMBER_ADDITION, ''));
  };

  const handleOnClickHouseAdditions = (inputValue: string) => {
    dispatch(setInput(HOUSE_NUMBER_ADDITION, inputValue));
    if (errorHouseNumberAddition?.missing) dispatch(setMissingError(HOUSE_NUMBER_ADDITION, ''));
  };

  const handleOnFocus = () => {
    setSuggestedHouseNumberAdditions(houseNumberAdditions);
    setSuggestionsOpen(true);
  };

  const validatePostalCode = () => {
    const regex = /^\d{4} ?[a-z]{2}$/i;
    return regex.test(valuePostCode);
  };

  const fetchAddress = (cancelToken: CancelToken) => {
    const { HOME_ADDRESS_LOOKUP_PATH } = getUrls();
    const formatPostCodeWithoutSpace = valuePostCode.replace(/\s/g, '').toLowerCase();
    dispatch(setHomeAddressRequestStatus('pending'));
    return axios
      .get(`${HOME_ADDRESS_LOOKUP_PATH}/nl/${formatPostCodeWithoutSpace}/${valueHouseNumber}`, {
        cancelToken,
      })
      .then((res: AxiosResponse) => {
        dispatch(setInput(CITY, res.data.city));
        dispatch(setInput(STREET, res.data.street));
        dispatch(setHomeAddressRequestStatus('idle'));
        dispatch(setInvalidError(HOME_ADDRESS, ''));
        if (res.data.houseNumberAdditions.length > 0) {
          const houseNumberAdditionCanBeEmpty = res.data.houseNumberAdditions.includes('');

          const formatedHouseNumbers = res.data.houseNumberAdditions
            .filter((item: any) => item)
            .map((number: IDropDownOption) => ({
              label: number,
              value: number,
            }));
          dispatch(
            setIsHouseNumberRequired(
              houseNumberAdditionCanBeEmpty ? false : formatedHouseNumbers.length > 1
            )
          );
          setHouseNumberAdditions(formatedHouseNumbers);
        }
      })
      .catch((err) => {
        if (valueCity || valueStreet) {
          dispatch(setInput(CITY, ''));
          dispatch(setInput(STREET, ''));
        }
        if (axios.isCancel(err)) return;
        dispatch(setHomeAddressRequestStatus('failed'));
        dispatch(setInvalidError(HOME_ADDRESS, noAddressFound));
        setHouseNumberAdditions([]);
        dispatch(setInput(HOUSE_NUMBER_ADDITION, ''));
      });
  };

  const isPostCodeValid = () => {
    dispatch(validateValue(validatePostalCode, POST_CODE, valuePostCode, locale));
  };

  useEffect(() => {
    const validPostalCode = validatePostalCode();
    if (validPostalCode && errorPostCode?.invalid) dispatch(setInvalidError(POST_CODE, ''));
  }, [valuePostCode]);

  useEffect(() => {
    const validHouseNumberAddition = validateAdditionalHouseNumber();
    if (validHouseNumberAddition && errorHouseNumberAddition?.invalid)
      dispatch(setInvalidError(HOUSE_NUMBER_ADDITION, ''));
    dispatch(setMissingError(HOUSE_NUMBER_ADDITION, ''));
    if (valueHouseNumberAddition)
      dispatch(
        validateValue(
          validateAdditionalHouseNumber,
          HOUSE_NUMBER_ADDITION,
          valueHouseNumberAddition,
          locale
        )
      );
  }, [valueHouseNumberAddition]);

  useEffect(() => {
    const validPostalCode = validatePostalCode();
    const cancelToken = axios.CancelToken;
    const source = cancelToken.source();
    if (validPostalCode && valueHouseNumber.length > 0) {
      fetchAddress(source.token);
    } else {
      if (valueStreet || valueCity) {
        dispatch(setInput(CITY, ''));
        dispatch(setInput(STREET, ''));
      }
      dispatch(setInvalidError(HOME_ADDRESS, ''));
    }
    return () => {
      source.cancel();
    };
  }, [debouncedPostCodeValue, debouncedHouseNumberValue]);

  useEffect(() => {
    if (isSuggestionsOpen) {
      document.addEventListener('mousedown', handleClick);
    } else {
      document.removeEventListener('mousedown', handleClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, [isSuggestionsOpen]);

  useEffect(() => {
    const filteredSuggestions = houseNumberAdditions.filter((number: IDropDownOption) =>
      String(number.value).includes(valueHouseNumberAddition)
    );
    setSuggestedHouseNumberAdditions(filteredSuggestions);

    if (
      suggestedHouseNumberAdditions.length > 0 &&
      suggestedHouseNumberAdditions.some(
        (suggestedNumber: IDropDownOption) => suggestedNumber.value === valueHouseNumberAddition
      )
    ) {
      setIsHouseNumberAdditionValid(true);
    } else {
      setIsHouseNumberAdditionValid(false);
    }
  }, [valueHouseNumberAddition]);

  useEffect(() => {
    if (showHouseNumberAdditionSuggestions) {
      setSuggestionsOpen(true);
    } else {
      setSuggestionsOpen(false);
    }
  }, [showHouseNumberAdditionSuggestions]);

  const hasError =
    errorPostCode?.missing ||
    errorPostCode?.invalid ||
    errorHomeAddress ||
    errorHouseNumber ||
    errorHouseNumberAddition?.invalid;

  return (
    <div
      className={getInputClassNames({
        tooltip: props.tooltip,
        error: hasError,
        componentName: props.componentName,
      })}
    >
      <div className="qred-first qred-component-header">
        <span className="qred-first">{label}</span>
        {tooltipText && <Tooltip tooltip={props.tooltip} tooltipText={tooltipText} />}
      </div>

      <>
        {homeAddressRequestIsPending && <LoadingSpinner />}
        <Input
          {...props}
          label={postCode}
          value={formatPostCode}
          error={errorPostCode?.missing || errorPostCode?.invalid}
          handleValidation={isPostCodeValid}
          onChange={handleOnChangePostCode}
          isValid={hasValidAddress}
          analyticsEvent="postCode"
          maxLength={7}
          className={`qred-postcode ${errorHomeAddress ? 'qred-error' : ''}`}
        />
      </>

      <>
        {homeAddressRequestIsPending && <LoadingSpinner />}
        <Input
          {...props}
          label={houseNumber}
          value={valueHouseNumber}
          error={errorHouseNumber}
          onChange={handleOnChangeHouseNumber}
          isValid={hasValidAddress}
          analyticsEvent="houseNumber"
          className={`qred-housenumber ${errorHomeAddress ? 'qred-error' : ''}`}
        />
      </>

      {showHouseNumberAddition && (
        <div className="qred-housenumberaddition-wrapper">
          <Input
            {...props}
            label={houseNumberAddition}
            analyticsEvent="houseNumberAddition"
            value={valueHouseNumberAddition}
            tooltip={false}
            onFocus={handleOnFocus}
            innerRef={refHouseNumberAdditionInput}
            isValid={isHouseNumberAdditionValid}
            autocomplete="new-password"
            error={errorHouseNumberAddition?.missing || errorHouseNumberAddition?.invalid}
            onChange={handleOnChangeHouseAdditions}
            className={`qred-housenumberaddition ${errorHomeAddress ? 'qred-error' : ''}`}
          />
          {isSuggestionsOpen && (
            <ul ref={refSuggestionList} className="qred-housenumberaddition-suggestions">
              {suggestedHouseNumberAdditions.map((number: IDropDownOption) => (
                <li key={`${number.label}`}>
                  <button
                    type="button"
                    tabIndex={0}
                    onClick={() => handleOnClickHouseAdditions(number.label)}
                  >
                    {number.label}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {errorHomeAddress && <ComponentError combinedError error={noAddressFound} />}

      <Input disabled {...props} label={street} value={valueStreet} analyticsEvent="street" />
      <Input disabled {...props} label={city} value={valueCity} analyticsEvent="city" />
    </div>
  );
};

export default HomeAddress;
